import type { CellDisplay, PersonOption } from "@bundle/core";
import { Avatar, CodeBadge, Icon, Stack, Text } from "../../primitives/index.ts";
import { useFormat, useT } from "../../i18n/I18nProvider.tsx";
import { Tag } from "./Tag.tsx";
import { ContactChips } from "../../mail/ContactCells.tsx";

export type CellValueProps = {
  display: CellDisplay;
  people: PersonOption[];
  /** Page id → title (relations). */
  titles: Record<string, string>;
  /** Board cards and list rows: one line, wraps tags instead of clipping. */
  compact?: boolean;
  /** Sheet cells: tags, people and relations stay on one line (the rest is clipped) instead of wrapping. */
  singleLine?: boolean;
  /** Shows "Empty" for empty cells (property panel); otherwise nothing. */
  showEmpty?: boolean;
  testID?: string;
};

/**
 * Read-only rendering of one cell: text, number, tags, status, dates, people, checkbox, files, relations, and formula
 * or rollup errors (the code in danger text, the message in a tooltip).
 */
export function CellValue({ display, people, titles, compact, singleLine, showEmpty, testID }: CellValueProps) {
  const t = useT();
  const fmt = useFormat();
  switch (display.kind) {
    case "empty":
      return showEmpty === true ? (
        <Text variant="footnote" color="text.tertiary" testID={testID}>
          {t("db.value.empty")}
        </Text>
      ) : (
        <Stack testID={testID} />
      );
    case "text":
      return (
        <Text variant="footnote" color={display.link === undefined ? "text.primary" : "text.link"} lines={compact === true ? 1 : 2} testID={testID}>
          {display.text}
        </Text>
      );
    case "number":
      return (
        <Text variant="footnote" lines={1} testID={testID}>
          {fmt.number(display.number, display.format)}
        </Text>
      );
    case "options":
      return (
        <Stack direction="horizontal" align="center" gap="1" wrap={singleLine !== true} testID={testID}>
          {display.options.map((o) => (
            <Tag key={o.id} name={o.name} color={o.color} />
          ))}
        </Stack>
      );
    case "status":
      return (
        <Stack direction="horizontal" align="center" testID={testID}>
          <Tag name={display.option.name} color={display.option.color} statusGroup={display.option.group} />
        </Stack>
      );
    case "date":
      return (
        <Text variant="footnote" lines={1} testID={testID}>
          {display.end === undefined ? fmt.date(display.start) : `${fmt.date(display.start)} → ${fmt.date(display.end)}`}
        </Text>
      );
    case "people":
      return (
        <Stack direction="horizontal" align="center" gap="2" wrap={singleLine !== true} testID={testID}>
          {display.userIds.map((id) => {
            const p = people.find((x) => x.userId === id);
            const name = p?.name ?? id;
            return (
              <Stack key={id} direction="horizontal" align="center" gap="1">
                <Avatar name={name} src={p?.avatarUrl} size="avatar.xs" square={p?.isAgent} />
                <Text variant="footnote" lines={1}>
                  {name}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      );
    case "checkbox":
      return (
        <Stack direction="horizontal" align="center" testID={testID}>
          <Icon name={display.checked ? "checkbox" : "check"} size="icon.sm" color={display.checked ? "accent.text" : "border.strong"} />
        </Stack>
      );
    case "files":
      return (
        <Stack direction="horizontal" align="center" gap="1" testID={testID}>
          <Icon name="attach" size="icon.sm" color="text.secondary" />
          <Text variant="footnote" color="text.secondary">
            {t("db.files.count", { count: display.count })}
          </Text>
        </Stack>
      );
    case "relation":
      return (
        <Stack direction="horizontal" align="center" gap="1" wrap={singleLine !== true} testID={testID}>
          {display.pageIds.map((id) => (
            <Stack key={id} direction="horizontal" align="center" gap="0.5">
              <Icon name="page" size="icon.sm" color="text.secondary" />
              <Text variant="footnote" lines={1}>
                {titles[id] === undefined || titles[id] === "" ? t("db.untitled") : titles[id]!}
              </Text>
            </Stack>
          ))}
        </Stack>
      );
    case "contacts":
      return <ContactChips contactIds={display.contactIds} compact={compact} testID={testID} />;
    case "error":
      return (
        <Stack direction="horizontal" align="center" testID={testID}>
          <CodeBadge text={display.error} variant="error" tooltip={display.message} testID={testID === undefined ? undefined : `${testID}-error`} />
        </Stack>
      );
    case "timestamp":
      return (
        <Text variant="footnote" color="text.secondary" lines={1} testID={testID}>
          {fmt.timestamp(display.at)}
        </Text>
      );
  }
}
